import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import type { NavItem } from '@vnetwork/datacentral-ui/shells'
import usePermissionsStore from '@/stores/usePermissionsStore'
import useAuthStore from '@/stores/useAuthStore'
import { SidebarType } from '@/types/shared'
import { sidebarMenus } from '@/app/nav'

type Permission = { rsname?: string; scopes?: string[] }

/**
 * Same rules the legacy sidebar applied via `withPermission`:
 * - `require_admin` → super-admin only
 * - `ignorePermission` → always visible
 * - leaf with `rsname`/`rsnames` + `require_scopes` → visible if ANY resource
 *   grants every required scope (or the user is admin)
 * - leaf without permission config → visible
 * - parent → visible if at least one child is visible
 */
export function canSeeMenu(
    item: SidebarType,
    permissions: Permission[],
    isAdmin: boolean,
    isChild = false,
    departmentId?: number
): boolean {
    if (item.require_admin && !isAdmin) return false
    if (
        item.allowed_department_ids &&
        !isAdmin &&
        (departmentId === undefined ||
            !item.allowed_department_ids.includes(departmentId))
    )
        return false
    if (item.ignorePermission) return true
    if (item.children?.length) {
        return item.children.some((child) =>
            canSeeMenu(child, permissions, isAdmin, true, departmentId)
        )
    }
    // Top-level leaves only honoured `rsname` in the legacy sidebar; children also `rsnames`.
    const resources =
        isChild && item.rsnames?.length
            ? item.rsnames
            : item.rsname
              ? [item.rsname]
              : []
    if (!resources.length || !item.require_scopes) return true
    if (isAdmin) return true
    return resources.some((res) => {
        const scopes = permissions.find((p) => p?.rsname === res)?.scopes ?? []
        return item.require_scopes!.every((s) => scopes.includes(s))
    })
}

/** `/dayoff-account` → `sidebar-dayoff-account` (product tours target these). */
const tourId = (path: string) =>
    path ? `sidebar-${path.replace(/^\//, '').replace(/\//g, '-')}` : undefined

const parentTourId = (title: string) =>
    `sidebar-parent-${title.toLowerCase().replace(/\s+/g, '-')}`

const attrs = (id?: string) => (id ? { 'data-tour': id } : undefined)

/** Some sidebar titles are lowercase in the locale files ("hợp đồng"). */
const capitalize = (s: string) =>
    s ? s.charAt(0).toUpperCase() + s.slice(1) : s

/** Longest sidebar path that prefixes the current URL → active nav item id. */
function findActiveId(items: NavItem[], pathname: string): string | undefined {
    let best: { id: string; len: number } | undefined
    const visit = (list: NavItem[]) => {
        for (const item of list) {
            if (item.items) visit(item.items)
            const href = item.external ? undefined : item.href
            if (!href) continue
            const match =
                href === '/'
                    ? pathname === '/'
                    : pathname === href || pathname.startsWith(`${href}/`)
            if (match && (!best || href.length > best.len))
                best = { id: item.id, len: href.length }
        }
    }
    visit(items)
    return best?.id
}

/** `sidebarMenus` config → DS `NavItem[]` (translated, permission-filtered) + active id. */
export function useSidebarNav() {
    const { t } = useTranslation()
    const { pathname } = useLocation()
    const { permissions, isAdmin } = usePermissionsStore()
    const departmentId = useAuthStore((state) => state.employee?.department_id)

    const nav = useMemo<NavItem[]>(() => {
        const toNav = (item: SidebarType, isChild: boolean): NavItem => {
            const hasChildren = !!item.children?.length
            return {
                id: item.path || item.title,
                label: capitalize(t(item.title)),
                icon: isChild ? undefined : item.icon,
                href: hasChildren ? undefined : item.path,
                external: item.external,
                hidden: !canSeeMenu(
                    item,
                    permissions,
                    isAdmin,
                    isChild,
                    departmentId
                ),
                attributes: attrs(
                    hasChildren ? parentTourId(item.title) : tourId(item.path)
                ),
                items: hasChildren
                    ? item.children!.map((c) => toNav(c, true))
                    : undefined,
            }
        }
        return sidebarMenus.map((item) => toNav(item, false))
    }, [t, permissions, isAdmin, departmentId])

    const activeId = useMemo(() => findActiveId(nav, pathname), [nav, pathname])
    return { nav, activeId }
}
